/**
 * JSON-LD for the public judge and contest surfaces - `EX-PUB02`.
 *
 * Builders, not markup: they return plain objects so the leak test can assert
 * on them without a DOM, and `components/seo/JsonLd.tsx` is the only place that
 * serializes one into a page.
 *
 * **Type choices, because the wrong `@type` is worse than none.**
 *
 * - A practice problem is a `LearningResource` with
 *   `learningResourceType: "Practice problem"`. It is not an `Article` (nobody
 *   reads it for the prose) and not a `Quiz` - Google's `Quiz` rich result
 *   describes flashcard-style Q&A with an answer in the markup, and a
 *   competitive-programming problem's answer is a program the judge runs.
 * - A contest is an `Event`. It has a start, an end, a URL you attend it at and
 *   an audience, which is precisely what `Event` is for, and
 *   `OnlineEventAttendanceMode` + `VirtualLocation` are the honest way to say
 *   it happens nowhere.
 *
 * **`inLanguage` is the whole point of the task.** Bilingual statements are the
 * differentiator, so each page declares the language it is actually rendered in
 * and hreflang names its twin. A single blob claiming both languages would
 * describe a document that does not exist.
 *
 * Nothing here is given non-public content: the callers fetch through routes
 * that serve `visibility: "public"` and nothing else, and a caller holding
 * something else does not build JSON-LD at all.
 */
export type JsonLdObject = Record<string, unknown>;

const ORGANIZATION: JsonLdObject = {
  "@type": "Organization",
  name: "Progsity",
};

/** schema.org expects a BCP 47 tag, not our route segment. */
export function bcp47(locale: string): string {
  return locale === "bn" ? "bn-BD" : "en";
}

/**
 * A statement is markdown, sometimes with HTML in it, and a `description` is
 * plain prose. Tags, entities and runs of whitespace collapse; the result is
 * clipped on a word boundary so a meta description never ends mid-word.
 */
export function plainTextExcerpt(raw: string, maxLength = 300): string {
  const text = raw
    .replace(/<[^>]*>/g, " ")
    .replace(/`{1,3}[^`]*`{1,3}/g, " ")
    .replace(/[*_>#|]+/g, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    // Stripping `**a**` leaves "a , and" - the gap a reader sees in a search
    // result. Punctuation reattaches to the word before it, in both scripts.
    .replace(/\s+([,.;:!?)\]}।])/g, "$1")
    .replace(/([([{])\s+/g, "$1")
    .trim();

  if (text.length <= maxLength) return text;
  const clipped = text.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > maxLength * 0.6 ? clipped.slice(0, lastSpace) : clipped).trimEnd()}…`;
}

export type ProblemJsonLdInput = {
  slug: string;
  title: string;
  description: string;
  difficulty: string;
  tags: readonly string[];
  locale: string;
  siteUrl: string;
};

export function buildProblemJsonLd(input: ProblemJsonLdInput): JsonLdObject {
  const base = input.siteUrl.replace(/\/$/, "");
  const url = `${base}/${input.locale}/judge/${input.slug}`;

  return {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    name: input.title,
    description: input.description,
    url,
    inLanguage: bcp47(input.locale),
    learningResourceType: "Practice problem",
    educationalLevel: input.difficulty,
    // Free to read and free to solve - the practice library has no paywall, and
    // saying so is the one structured-data claim a crawler can act on.
    isAccessibleForFree: true,
    ...(input.tags.length > 0 ? { keywords: input.tags.join(", ") } : {}),
    provider: ORGANIZATION,
    isPartOf: {
      "@type": "CollectionPage",
      name: "Progsity Online Judge",
      url: `${base}/${input.locale}/judge`,
    },
  };
}

export type ContestJsonLdInput = {
  slug: string;
  title: string;
  description: string | null;
  startAt: string | null;
  endAt: string | null;
  locale: string;
  siteUrl: string;
};

export function buildContestJsonLd(input: ContestJsonLdInput): JsonLdObject {
  const base = input.siteUrl.replace(/\/$/, "");
  const url = `${base}/${input.locale}/contests/${input.slug}`;

  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: input.title,
    ...(input.description ? { description: input.description } : {}),
    url,
    inLanguage: bcp47(input.locale),
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    // A scheduled contest is `EventScheduled`; a contest whose window this page
    // does not know is not described as scheduled at all rather than guessed at.
    ...(input.startAt ? { eventStatus: "https://schema.org/EventScheduled" } : {}),
    ...(input.startAt ? { startDate: input.startAt } : {}),
    ...(input.endAt ? { endDate: input.endAt } : {}),
    location: { "@type": "VirtualLocation", url },
    organizer: ORGANIZATION,
    isAccessibleForFree: true,
  };
}

export function buildCollectionJsonLd(input: {
  name: string;
  description: string;
  path: string;
  locale: string;
  siteUrl: string;
  /**
   * `EX-TAG01`: the page's items in order, as an `ItemList` under
   * `mainEntity`. Paths are locale-less, like `path`.
   */
  items?: ReadonlyArray<{ name: string; path: string }>;
}): JsonLdObject {
  const base = input.siteUrl.replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: `${base}/${input.locale}${input.path}`,
    inLanguage: bcp47(input.locale),
    isPartOf: { "@type": "WebSite", name: "Progsity", url: base },
    ...(input.items && input.items.length > 0
      ? {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: input.items.length,
            itemListElement: input.items.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.name,
              url: `${base}/${input.locale}${item.path}`,
            })),
          },
        }
      : {}),
  };
}

/**
 * `EX-TAG01`: where a page sits (docs/12 §7), each crumb a locale-less path.
 * The last crumb is the page itself.
 */
export function buildBreadcrumbJsonLd(input: {
  crumbs: ReadonlyArray<{ name: string; path: string }>;
  locale: string;
  siteUrl: string;
}): JsonLdObject {
  const base = input.siteUrl.replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: input.crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: `${base}/${input.locale}${crumb.path}`,
    })),
  };
}
